import { UserRole, ROLE_PRIORITY, ROLE_PERMISSIONS } from '../types/restaurant';

type PermissionKey = keyof typeof ROLE_PERMISSIONS[UserRole];

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Validates if a role exists in ROLE_PERMISSIONS
 * @param userRole The role to validate
 * @returns boolean indicating if the role is valid
 */
const isValidRole = (userRole: UserRole | null): boolean => {
  if (!userRole) return false;
  const isValid = userRole in ROLE_PERMISSIONS;
  
  if (!isValid && isDevelopment) {
    console.warn(`Unknown role encountered: "${userRole}". Valid roles are: ${Object.keys(ROLE_PERMISSIONS).join(', ')}`);
  }
  
  return isValid;
};

/**
 * Generic permission checker that handles null roles and permission access
 * @param userRole The user's current role
 * @param permission The permission key to check
 * @returns boolean indicating if the user has the permission
 */
const getPermission = (userRole: UserRole | null, permission: PermissionKey): boolean => {
  if (!isValidRole(userRole)) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole as UserRole];
  const hasPermission = rolePermissions[permission];
  
  if (isDevelopment && hasPermission === undefined) {
    console.warn(`Permission "${permission}" not found for role "${userRole}"`);
  }
  
  return !!hasPermission;
};

/**
 * Checks if a user has the required role or higher
 * @param userRole The user's current role
 * @param requiredRole The minimum role required
 * @returns boolean indicating if the user has sufficient permissions
 */
export const hasPermission = (userRole: UserRole | null, requiredRole: UserRole): boolean => {
  if (!isValidRole(userRole)) return false;
  
  const userRolePriority = ROLE_PRIORITY[userRole as UserRole];
  const requiredRolePriority = ROLE_PRIORITY[requiredRole];
  
  if (isDevelopment && (userRolePriority === undefined || requiredRolePriority === undefined)) {
    console.warn(`Priority not found for role(s): userRole="${userRole}", requiredRole="${requiredRole}"`);
  }
  
  return userRolePriority >= requiredRolePriority;
};

/**
 * Checks if a user can manage staff
 * @param userRole The user's current role
 * @returns boolean indicating if the user can manage staff
 */
export const canManageStaff = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canManageStaff');

/**
 * Checks if a user can manage restaurant settings
 * @param userRole The user's current role
 * @returns boolean indicating if the user can manage settings
 */
export const canManageSettings = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canManageSettings');

/**
 * Checks if a user can manage the menu
 * @param userRole The user's current role
 * @returns boolean indicating if the user can manage menu
 */
export const canManageMenu = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canManageMenu');

/**
 * Checks if a user can manage tables
 * @param userRole The user's current role
 * @returns boolean indicating if the user can manage tables
 */
export const canManageTables = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canManageTables');

/**
 * Checks if a user can view reports
 * @param userRole The user's current role
 * @returns boolean indicating if the user can view reports
 */
export const canViewReports = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canViewReports');

/**
 * Checks if a user can manage inventory
 * @param userRole The user's current role
 * @returns boolean indicating if the user can manage inventory
 */
export const canManageInventory = (userRole: UserRole | null): boolean => 
  getPermission(userRole, 'canManageInventory'); 